import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalError } from '../utils/alert';
import { Modal } from 'react-bootstrap';
import './CashFlowReport.css';
import '../components/RevenueDesktop.css';
import { toLocalDateInput } from '@sf/shared';

const getFirstDayOfMonth = () => {
    const now = new Date();
    return toLocalDateInput(new Date(now.getFullYear(), now.getMonth(), 1));
};

const getLastDayOfMonth = () => {
    const now = new Date();
    return toLocalDateInput(new Date(now.getFullYear(), now.getMonth() + 1, 0));
};

const CashFlowReport = () => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showFilterModal, setShowFilterModal] = useState(false);
    const width = useWindowWidth();
    const isMobile = width < 768;

    // Filter states
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getLastDayOfMonth());
    const [revenueStatus, setRevenueStatus] = useState({ 'Recebida': true, 'Pendente': true });
    const [expenseStatus, setExpenseStatus] = useState({ 'Paga': true, 'Pendente': true, 'Atrasada': true });
    const [banks, setBanks] = useState([]);
    const [selectedBankIds, setSelectedBankIds] = useState([]);
    const [dateType, setDateType] = useState('due_date');

    useEffect(() => {
        api.get('/banks').then(res => {
            setBanks(res.data);
            setSelectedBankIds(res.data.map(b => b.id));
        });
    }, []);

    const handleRevenueStatusChange = (e) => {
        setRevenueStatus({ ...revenueStatus, [e.target.name]: e.target.checked });
    };

    const handleExpenseStatusChange = (e) => {
        setExpenseStatus({ ...expenseStatus, [e.target.name]: e.target.checked });
    };

    const handleBankSelectionChange = (bankId) => {
        const newSelection = selectedBankIds.includes(bankId)
            ? selectedBankIds.filter(id => id !== bankId)
            : [...selectedBankIds, bankId];
        setSelectedBankIds(newSelection);
    };

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);

        const selectedRevenueStatus = Object.keys(revenueStatus).filter(k => revenueStatus[k]);
        const selectedExpenseStatus = Object.keys(expenseStatus).filter(k => expenseStatus[k]);

        const params = new URLSearchParams();
        params.append('start_date', startDate);
        params.append('end_date', endDate);
        params.append('date_type', dateType);
        selectedRevenueStatus.forEach(s => params.append('revenue_status[]', s));
        selectedExpenseStatus.forEach(s => params.append('expense_status[]', s));
        selectedBankIds.forEach(id => params.append('bank_ids[]', id));

        try {
            const response = await api.get(`/reports/cash-flow?${params.toString()}`);
            setReportData(response.data);
        } catch (err) {
            swalError('Falha ao buscar relatório de fluxo de caixa.');
            setError('Falha ao buscar relatório de fluxo de caixa.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, revenueStatus, expenseStatus, selectedBankIds, dateType]);

    useEffect(() => {
        if (banks.length > 0) {
            fetchReport();
        }
    }, [fetchReport, banks]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    }

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    }

    const handleApplyFilters = () => {
        fetchReport();
        if (isMobile) {
            setShowFilterModal(false);
        }
    };

    // Componente de filtros reutilizável
    const FiltersContent = () => (
        <div className="cash-flow-filters">
            <div className="row g-3">
                <div className="col-md-3 col-6">
                    <label htmlFor="startDate" className="form-label">Data Início</label>
                    <input type="date" className="form-control" id="startDate" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="col-md-3 col-6">
                    <label htmlFor="endDate" className="form-label">Data Fim</label>
                    <input type="date" className="form-control" id="endDate" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <div className="col-md-2 col-12">
                    <label className="form-label">Tipo de Data</label>
                    <div className="form-check">
                        <input className="form-check-input" type="radio" name="dateType" id="dueDate" value="due_date" checked={dateType === 'due_date'} onChange={e => setDateType(e.target.value)} />
                        <label className="form-check-label" htmlFor="dueDate">Vencimento</label>
                    </div>
                    <div className="form-check">
                        <input className="form-check-input" type="radio" name="dateType" id="paymentDate" value="payment_date" checked={dateType === 'payment_date'} onChange={e => setDateType(e.target.value)} />
                        <label className="form-check-label" htmlFor="paymentDate">Pagamento</label>
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <label className="form-label">Status Receitas</label>
                    {Object.keys(revenueStatus).map(status => (
                        <div className="form-check" key={status}>
                            <input className="form-check-input" type="checkbox" name={status} checked={revenueStatus[status]} onChange={handleRevenueStatusChange} />
                            <label className="form-check-label">{status}</label>
                        </div>
                    ))}
                </div>
                <div className="col-md-2 col-6">
                    <label className="form-label">Status Despesas</label>
                    {Object.keys(expenseStatus).map(status => (
                        <div className="form-check" key={status}>
                            <input className="form-check-input" type="checkbox" name={status} checked={expenseStatus[status]} onChange={handleExpenseStatusChange} />
                            <label className="form-check-label">{status}</label>
                        </div>
                    ))}
                </div>
            </div>
            <div className="row g-3 mt-2">
                <div className="col-12">
                    <label className="form-label d-block">Bancos:</label>
                    <div className="banks-filter-group">
                        {banks.map(bank => (
                            <div className="form-check form-check-inline" key={bank.id}>
                                <input className="form-check-input" type="checkbox" name={bank.name} checked={selectedBankIds.includes(bank.id)} onChange={() => handleBankSelectionChange(bank.id)} />
                                <label className="form-check-label">{bank.name}</label>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-12">
                    <button className="btn btn-primary w-100" onClick={handleApplyFilters} disabled={loading}>
                        {loading ? 'Carregando...' : 'Aplicar Filtros'}
                    </button>
                </div>
            </div>
        </div>
    );

    // Renderização Mobile - Cards
    const renderMobileView = () => (
        <div className="cash-flow-mobile">
            {/* Header com botão de filtros */}
            <div className="mobile-header">
                <button className="btn btn-outline-primary" onClick={() => setShowFilterModal(true)}>
                    Filtros
                </button>
                <div className="date-range-badge">
                    {formatDate(startDate)} - {formatDate(endDate)}
                </div>
            </div>

            {/* Modal de Filtros */}
            <Modal show={showFilterModal} onHide={() => setShowFilterModal(false)} fullscreen="sm-down">
                <Modal.Header closeButton>
                    <Modal.Title>Filtros</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <FiltersContent />
                </Modal.Body>
            </Modal>

            {loading && <div className="text-center py-4"><div className="spinner-border" role="status"></div></div>}

            {reportData && !loading && (
                <>
                    {/* Saldo Inicial */}
                    <div className="balance-card initial">
                        <span className="balance-label">Saldo Inicial</span>
                        <span className="balance-value">{formatCurrency(reportData.initial_balance)}</span>
                    </div>

                    {/* Lista de Transações */}
                    <div className="transactions-list">
                        {reportData.transactions.map((item, index) => {
                            const isProjected = item.type === 'expense_projected';
                            const isRevenue = item.type === 'revenue';
                            const amount = Math.abs(item.amount);

                            return (
                                <div key={index} className={`transaction-card ${isProjected ? 'projected' : ''} ${isRevenue ? 'revenue' : 'expense'}`}>
                                    <div className="transaction-main">
                                        <div className="transaction-info">
                                            <span className="transaction-date">{formatDate(item.date)}</span>
                                            <span className="transaction-description">{item.description}</span>
                                        </div>
                                        <div className="transaction-values">
                                            <span className={`transaction-amount ${isRevenue ? 'positive' : 'negative'}`}>
                                                {isRevenue ? '+' : '-'} {formatCurrency(amount)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="transaction-balance">
                                        <span className="balance-label">Saldo:</span>
                                        <span className="balance-value">{formatCurrency(item.balance)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Saldo Final */}
                    {reportData.transactions.length > 0 && (
                        <div className="balance-card final">
                            <span className="balance-label">Saldo Final</span>
                            <span className="balance-value">
                                {formatCurrency(reportData.transactions[reportData.transactions.length - 1].balance)}
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // Renderização Desktop - Tabela (original)
    const renderDesktopView = () => (
        <div className="container mt-4">
            <div className="rv-page-header">
                <div className="rv-page-header-title">
                    <h1>Fluxo de Caixa</h1>
                    <p>Extrato detalhado de entradas e saídas</p>
                </div>
            </div>

            <div className="card mb-4">
                <div className="card-header">
                    Filtros
                </div>
                <div className="card-body">
                    <div className="row g-3">
                        <div className="col-md-3">
                            <label htmlFor="startDate" className="form-label">Data Início</label>
                            <input type="date" className="form-control" id="startDate" value={startDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div className="col-md-3">
                            <label htmlFor="endDate" className="form-label">Data Fim</label>
                            <input type="date" className="form-control" id="endDate" value={endDate} onChange={e => setEndDate(e.target.value)} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Tipo de Data</label>
                            <div className="form-check">
                                <input className="form-check-input" type="radio" name="dateType" id="dueDate" value="due_date" checked={dateType === 'due_date'} onChange={e => setDateType(e.target.value)} />
                                <label className="form-check-label" htmlFor="dueDate">Vencimento</label>
                            </div>
                            <div className="form-check">
                                <input className="form-check-input" type="radio" name="dateType" id="paymentDate" value="payment_date" checked={dateType === 'payment_date'} onChange={e => setDateType(e.target.value)} />
                                <label className="form-check-label" htmlFor="paymentDate">Pagamento</label>
                            </div>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Status Receitas</label>
                            {Object.keys(revenueStatus).map(status => (
                                <div className="form-check" key={status}>
                                    <input className="form-check-input" type="checkbox" name={status} checked={revenueStatus[status]} onChange={handleRevenueStatusChange} />
                                    <label className="form-check-label">{status}</label>
                                </div>
                            ))}
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Status Despesas</label>
                            {Object.keys(expenseStatus).map(status => (
                                <div className="form-check" key={status}>
                                    <input className="form-check-input" type="checkbox" name={status} checked={expenseStatus[status]} onChange={handleExpenseStatusChange} />
                                    <label className="form-check-label">{status}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="row g-3 mt-2">
                        <div className="col-12">
                            <label className="form-label me-3">Bancos:</label>
                            {banks.map(bank => (
                                <div className="form-check form-check-inline" key={bank.id}>
                                    <input className="form-check-input" type="checkbox" name={bank.name} checked={selectedBankIds.includes(bank.id)} onChange={() => handleBankSelectionChange(bank.id)} />
                                    <label className="form-check-label">{bank.name}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-12">
                            <button className="btn btn-primary" onClick={fetchReport} disabled={loading}>
                                {loading ? 'Carregando...' : 'Aplicar Filtros'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {loading && <div>Carregando relatório...</div>}

            {reportData && !loading && (
                <table className="table table-striped table-hover">
                    <thead className="thead-dark">
                        <tr>
                            <th>Data</th>
                            <th>Descrição</th>
                            <th className="text-end">Entradas</th>
                            <th className="text-end">Saídas</th>
                            <th className="text-end">Saldo</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td colSpan="4" className="text-end"><strong>Saldo Inicial</strong></td>
                            <td className="text-end"><strong>{formatCurrency(reportData.initial_balance)}</strong></td>
                        </tr>
                        {reportData.transactions.map((item, index) => {
                            const isProjected = item.type === 'expense_projected';
                            const expenseAmount = (item.type === 'expense' || isProjected) ? Math.abs(item.amount) : null;

                            return (
                                <tr key={index} className={isProjected ? 'text-muted fst-italic' : ''}>
                                    <td>{formatDate(item.date)}</td>
                                    <td>{item.description}</td>
                                    <td className="text-end text-success">
                                        {item.type === 'revenue' ? formatCurrency(item.amount) : '-'}
                                    </td>
                                    <td className="text-end text-danger">
                                        {expenseAmount !== null ? formatCurrency(expenseAmount) : '-'}
                                    </td>
                                    <td className="text-end">{formatCurrency(item.balance)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );

    return isMobile ? renderMobileView() : renderDesktopView();
};

export default CashFlowReport;
